import { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../config/apiBase';
import { adminFetchInit } from '../../config/adminApi';
import { ADMIN_BTN_GHOST, ADMIN_CARD, ADMIN_CARD_MUTED } from './adminTheme';

function formatTs(ts) {
  if (!ts) return '—';
  try {
    return new Date(Number(ts) * 1000).toLocaleString();
  } catch {
    return String(ts);
  }
}

function stepLabel(step) {
  const labels = {
    go_pressed: 'Go pressed',
    dns_check_start: 'DNS check start',
    dns_check_done: 'DNS check done',
    preprocess_start: 'Preprocess start',
    preprocess_done: 'Preprocess done',
    handle_search_start: 'Handle search / SSE request',
    stream_gate_start: 'Stream gate start',
    stream_gate_done: 'Stream gate done',
    sse_open: 'SSE connected',
    sse_first_event: 'First SSE event',
    scan_complete: 'Scan complete',
  };
  return labels[step] || step;
}

function TraceRow({ trace, expanded, onToggle }) {
  const steps = Array.isArray(trace.steps) ? trace.steps : [];
  const lastDelta = steps.length ? steps[steps.length - 1].delta_ms : 0;
  const causes = Array.isArray(trace.delay_causes) ? trace.delay_causes : [];

  return (
    <div className={ADMIN_CARD_MUTED}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-3 px-1 py-1 text-left transition hover:bg-white/5"
      >
        <div className="min-w-0">
          <p className="truncate font-semibold text-slate-100" title={trace.query_hint}>
            {trace.query_hint || '(no query)'}
          </p>
          <p className="mt-0.5 font-mono text-[10px] text-slate-500">
            {trace.tab} · {trace.source} · {trace.endpoint || 'pending'}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <p className="font-mono text-[11px] font-bold text-cyan-300">{lastDelta} ms</p>
          <p className="text-[10px] text-slate-500">{expanded ? '[hide]' : '[show]'}</p>
        </div>
      </button>
      {expanded ? (
        <div className="mt-2 border-t border-white/10 px-1 pt-2">
          <p className="mb-2 font-mono text-[10px] text-slate-500">
            trace {trace.trace_id?.slice(0, 8)}… · {formatTs(trace.created_ts)}
          </p>
          {causes.length > 0 ? (
            <ul className="mb-2 list-disc space-y-1 pl-4 text-[11px] text-amber-200">
              {causes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          ) : (
            <p className="mb-2 text-[11px] text-emerald-300">No significant delay detected.</p>
          )}
          <ol className="space-y-1">
            {steps.map((s, idx) => (
              <li
                key={`${s.step}-${idx}`}
                className="flex flex-wrap items-baseline justify-between gap-2 rounded border border-white/10 bg-slate-950/60 px-2 py-1 font-mono text-[10px]"
              >
                <span className="font-semibold text-slate-200">{stepLabel(s.step)}</span>
                <span className="text-cyan-300">+{s.delta_ms} ms</span>
                {s.detail ? <span className="w-full text-slate-500">{s.detail}</span> : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

export default function AdminFrontendSearchTraces({ pollMs = 4000 }) {
  const base = getApiBaseUrl();
  const [traces, setTraces] = useState([]);
  const [expandedId, setExpandedId] = useState('');
  const [error, setError] = useState('');

  const loadTraces = useCallback(async () => {
    try {
      const res = await fetch(`${base}/admin/frontend-search-traces?limit=15`, adminFetchInit());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTraces(Array.isArray(data?.traces) ? data.traces : []);
      setError('');
    } catch (e) {
      setError(String(e?.message || e));
    }
  }, [base]);

  useEffect(() => {
    loadTraces();
    const id = window.setInterval(loadTraces, pollMs);
    return () => window.clearInterval(id);
  }, [loadTraces, pollMs]);

  return (
    <section className={ADMIN_CARD}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-xs font-black uppercase tracking-[0.16em] text-cyan-300">
            Frontend search timing
          </h3>
          <p className="mt-1 text-[11px] text-slate-400">
            Go press -&gt; preprocess/DNS -&gt; stream gate -&gt; first backend SSE event (delay causes).
          </p>
        </div>
        <button type="button" onClick={loadTraces} className={ADMIN_BTN_GHOST}>
          Refresh
        </button>
      </div>
      {error ? <p className="mb-2 text-[11px] text-rose-300">{error}</p> : null}
      <div className="max-h-[420px] space-y-2 overflow-y-auto overscroll-contain pr-1" data-testid="admin-frontend-traces-scroll">
        {traces.map((trace) => (
          <TraceRow
            key={trace.trace_id}
            trace={trace}
            expanded={expandedId === trace.trace_id}
            onToggle={() =>
              setExpandedId((prev) => (prev === trace.trace_id ? '' : trace.trace_id))
            }
          />
        ))}
        {traces.length === 0 && !error ? (
          <p className="py-6 text-center text-sm text-slate-500">No frontend traces yet — run a Home search.</p>
        ) : null}
      </div>
    </section>
  );
}
